/*
 * @author Andrey Patyuchenko
 * @email  apatyuchenko@atlassian.com
 * @email  andrey.patyuchenko@gmail.com
 * @date   3/11/22
 */

import React from "react";
import GoogleIcon from "@mui/icons-material/Google";
import { LoadingButton } from "@mui/lab";
import { addSeconds, isAfter } from "date-fns";
import { CalendarView, Employee, GoogleEvent } from "../interfaces/Global";
import axios from "axios";
import { GoogleCalendarEvent, GoogleCalendarEventsListResponse } from "../interfaces/GoogleCalendar";
import { useDispatch, useSelector } from "react-redux";
import { State } from "../state/store";
import { set } from "../state/reducers/google-events";
import zonedTimeToUtc from "date-fns-tz/zonedTimeToUtc";
import { Tooltip } from "@mui/material";
import { toast } from "react-toastify";

const GOOGLE_AUTH_STORAGE = "google.auth";

interface GoogleAuth {
	access_token: string;
	expires_in: number;
	expires: string;
	hd: string;
	prompt: string;
	token_type: string;
	scopes: string;
	state: string;
	error: string;
}

const processEventLists = (employees: Employee[], responses: GoogleCalendarEventsListResponse[]) => {
	const events: GoogleEvent[] = [];
	employees.forEach((employee, i) => {
		const employeeId = employee.id, r = responses[i];
		r.items
			.filter(e => !e.transparency || e.transparency === "opaque")
			.filter(e => e.attendees?.find(a => a.email === employee.emailAddress)?.responseStatus !== "declined")
			.forEach(e => {
				const startDatetime = e.start.date ? zonedTimeToUtc(e.start.date, r.timeZone || "UTC") : new Date(e.start.dateTime || 0);
				const endDatetime = e.end.date ? zonedTimeToUtc(e.end.date, r.timeZone || "UTC") : new Date(e.end.dateTime || 0);
				events.push({
					id: employeeId + e.id,
					creationDate: e.created ? new Date(e.created).getTime() : 0,
					createdBy: e.creator?.email?.substring(0, e.creator?.email?.indexOf("@")) || employeeId,
					employeeId,
					startDatetime: startDatetime.getTime(),
					endDatetime: endDatetime.getTime(),
					htmlLink: e.htmlLink,
					summary: e.summary,
					typeId: getTypeId(e),
					isUnavailable: true,
					isConfirmed: Boolean(
						e.organizer?.email === r.summary ||
						e.attendees?.find(a => a.email === r.summary && a.responseStatus === "accepted")
					)
				});
			});
	});
	return events;
};

const getTypeId = (event: GoogleCalendarEvent) => {
	if (event.attendees && event.attendees.length > 1) {
		let isExternal = event.attendees.some(attendee => !attendee.email?.includes("@atlassian.com"));
		return isExternal ? "EM" : "IM";
	}
	const kw: Record<string, string[]> = {
		CE: [ "townhall", "town hall" ],
		SHP: [ "shipit", "ship it" ],
		FE: [ "foundation" ],
		OOO: [ "ooo", "out of office", "leave" ],
		SHA: [ "shadowing" ],
		KCS: [ "kcs" ],
		ESC: [ "escalation" ],
		D: [ "disturbed" ],
		ML: [ "doctor", "dentist" ],
		AFK: [ "lunch", "dinner", "break", "away" ]
	};
	if (event.summary) {
		return Object.keys(kw).find(typeId => kw[typeId].some(w => event.summary?.toLowerCase().includes(w))) || "BSY";
	}
	return "BSY";
};

export default function GSyncButton({ employees, view }: { employees: Employee[], view: CalendarView }) {

	const user = useSelector((s: State) => s.user);
	const range = useSelector((s: State) => s.range);
	const displayGoogleEvents = useSelector((s: State) => s.settings.displayGoogleEvents ?? true);
	const monthEventMinDurationHours = useSelector((s: State) => s.settings.monthEventMinDurationHours ?? true);
	const [ isLoading, setIsLoading ] = React.useState(false);
	const dispatch = useDispatch();

	const doSync = (token: string) => {
		setIsLoading(true);
		let requests = employees.map(employee => {
			const url = `https://www.googleapis.com/calendar/v3/calendars/${employee.emailAddress}/events`;
			const config = { headers: { "Authorization": "Bearer " + token } };
			const params = {
				timeMin: new Date(range.start),
				timeMax: new Date(range.end),
				singleEvents: true,
				showDeleted: false,
				maxAttendees: 1,
				maxResults: 2500
			};
			return axios.get<GoogleCalendarEventsListResponse>(url, { ...config, params })
				.catch(err => {
					toast.error("Error retrieving Google Calendar data for " + employee.displayName + ": " + err.message);
					return {
						data: {
							summary: employee.emailAddress as string,
							timeZone: "UTC",
							items: []
						} as GoogleCalendarEventsListResponse
					};
				});
		});
		return Promise.all(requests)
			.then(r => r.map(l => l.data))
			.then((r) => processEventLists(employees, r))
			.then(events => {
				dispatch(set(events));
				toast.success(`Successfully imported ${events.length} events from Google Calendar.`
					+ (view === "month" ?
						` Showing ${monthEventMinDurationHours ? ` only events with duration of at least ${monthEventMinDurationHours} hours` :
							"all events with no duration filter"}` : '')
				);
			}).catch(err => {
				console.error(err);
				toast.error("Error retrieving Google Calendar data: " + err);
			}).finally(() => setIsLoading(false));
	};

	const doAuthSync = async () => {
		return new Promise<GoogleCalendarEventsListResponse[]>((ok, ko) => {
			// @ts-ignore
			const client = google.accounts.oauth2.initTokenClient({
				client_id: process.env.GOOGLE_CLIENT_ID || "269580997490-i7307i9qtflqr83541fnsqcvgbi06cu1.apps.googleusercontent.com",
				scope: "https://www.googleapis.com/auth/calendar.events.readonly",
				hint: user?.id + "@atlassian.com",
				prompt: "",
				hosted_domain: "atlassian.com",
				callback: (data: GoogleAuth) => {
					if (data.error) {
						console.error("Google login error", data);
						ko(data);
					} else {
						console.log("Google login successful, expires in", data.expires_in);
						const expires = addSeconds(new Date(), data.expires_in);
						localStorage.setItem(
							GOOGLE_AUTH_STORAGE,
							JSON.stringify({ ...data, expires: expires.toISOString() } as GoogleAuth)
						);
						doSync(data.access_token).catch(ko);
					}
				},
			});
			client.requestAccessToken();
		});
	};

	return (
		<Tooltip title={"Sync with Google Calendar"}>
			<LoadingButton
				loading={isLoading}
				variant={"outlined"}
				size={"large"}
				disabled={!displayGoogleEvents}
				startIcon={<GoogleIcon/>}
				onClick={() => {
					const gAuth = JSON.parse(localStorage.getItem(GOOGLE_AUTH_STORAGE) || "{}") as GoogleAuth;
					if (!gAuth.expires || isAfter(new Date(), new Date(gAuth.expires))) {
						doAuthSync().then();
					} else {
						doSync(gAuth.access_token).then();
					}
				}}
			>
				Import
			</LoadingButton>
		</Tooltip>
	);

}
